import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { CreatePostData, Post } from '../types';

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePostData) => void;
  userAvatar?: string;
  userName: string;
  allowedVisibilities?: Post['visibility'][];
  initialVisibility?: Post['visibility'];
  classOptions?: Array<{ value: number; label: string }>;
  initialCategory?: Post['category'];
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userAvatar,
  userName,
  allowedVisibilities,
  initialVisibility,
  classOptions = [],
  initialCategory,
}) => {
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<Post['category']>('discussion');
  const [visibility, setVisibility] = useState<Post['visibility']>(initialVisibility ?? 'school');
  const [targetClassroomId, setTargetClassroomId] = useState<number | ''>('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<File[]>([]);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);

  const [eventTitle, setEventTitle] = useState('');
  const [eventStartAt, setEventStartAt] = useState('');
  const [eventEndAt, setEventEndAt] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventCapacity, setEventCapacity] = useState<string>('');

  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      if (initialCategory) setCategory(initialCategory);
    }
    wasOpenRef.current = isOpen;
  }, [initialCategory, isOpen]);

  useEffect(() => {
    if (visibility !== 'class') {
      if (targetClassroomId !== '') setTargetClassroomId('');
      return;
    }

    const ids = new Set((classOptions || []).map((c) => Number(c.value)));
    const current = targetClassroomId === '' ? null : Number(targetClassroomId);

    if (current != null && !ids.has(current)) {
      setTargetClassroomId('');
      return;
    }

    if ((targetClassroomId === '' || targetClassroomId == null) && classOptions.length === 1) {
      setTargetClassroomId(Number(classOptions[0].value));
    }
  }, [classOptions, targetClassroomId, visibility]);

  const categoryOptions = [
    { value: 'announcement', label: 'Anunț' },
    { value: 'achievement', label: 'Realizare' },
    { value: 'event', label: 'Eveniment' },
    { value: 'discussion', label: 'Discuție' },
    { value: 'resource', label: 'Resursă' },
  ];

  const visibilityOptions = [
    { value: 'school', label: 'Doar școala' },
    { value: 'class', label: 'Clasele mele' },
    { value: 'public', label: 'Public' },
  ];

  const effectiveVisibilityOptions = allowedVisibilities?.length
    ? visibilityOptions.filter((o) => allowedVisibilities.includes(o.value as Post['visibility']))
    : visibilityOptions;

  useEffect(() => {
    if (!isOpen) return;
    if (!initialVisibility) return;
    if (allowedVisibilities?.length && !allowedVisibilities.includes(initialVisibility)) return;
    setVisibility(initialVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedVisibilities, isOpen, initialVisibility]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length + selectedImages.length > 5) {
      alert('Sunt permise maximum 5 imagini');
      return;
    }

    setSelectedImages([...selectedImages, ...files]);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleAttachmentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const file = files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      alert('Video prea mare (max 50MB).');
      return;
    }
    if (videoPreview) {
      try {
        URL.revokeObjectURL(videoPreview);
      } catch {
        // ignore
      }
    }
    setSelectedVideos([file]);
    try {
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
    } catch {
      setVideoPreview(null);
    }
  };

  const removeImage = (index: number) => {
    setSelectedImages(selectedImages.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  const removeAttachment = (index: number) => {
    const next = selectedVideos.filter((_, i) => i !== index);
    setSelectedVideos(next);
    if (!next.length && videoPreview) {
      try {
        URL.revokeObjectURL(videoPreview);
      } catch {
        // ignore
      }
      setVideoPreview(null);
    }
  };

  const handleAddTag = () => {
    const next = tagInput.trim();
    if (next && !tags.includes(next) && tags.length < 5) {
      setTags([...tags, next]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleReset = () => {
    setContent('');
    setCategory('discussion');
    setVisibility(initialVisibility ?? 'school');
    setTargetClassroomId('');
    setTags([]);
    setTagInput('');
    setSelectedImages([]);
    setImagePreviews([]);
    setSelectedVideos([]);
    if (videoPreview) {
      try {
        URL.revokeObjectURL(videoPreview);
      } catch {
        // ignore
      }
    }
    setVideoPreview(null);
    setEventTitle('');
    setEventStartAt('');
    setEventEndAt('');
    setEventLocation('');
    setEventCapacity('');
  };

  const handleSubmit = () => {
    if (!content.trim()) {
      alert('Te rog introdu conținutul postării');
      return;
    }

    if (visibility === 'class' && !targetClassroomId) {
      alert(classOptions.length > 0 ? 'Selectează o clasă.' : 'Nu ai nicio clasă disponibilă pentru postare.');
      return;
    }
    if (category === 'event' && !eventTitle.trim()) {
      alert('Completează titlul evenimentului.');
      return;
    }

    const postData: CreatePostData = {
      content: content.trim(),
      images: selectedImages,
      videos: selectedVideos,
      category,
      visibility,
      tags,
      targetClassroomId:
        visibility === 'class' ? (targetClassroomId ? Number(targetClassroomId) : null) : null,
      eventTitle: category === 'event' ? eventTitle.trim() : undefined,
      eventStartAt: category === 'event' ? eventStartAt || undefined : undefined,
      eventEndAt: category === 'event' ? eventEndAt || undefined : undefined,
      eventLocation: category === 'event' ? (eventLocation.trim() || undefined) : undefined,
      eventCapacity: category === 'event' ? (eventCapacity ? Number(eventCapacity) : null) : undefined,
    };

    onSubmit(postData);
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[1040] animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
          <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
            <h2 className="text-lg font-semibold text-foreground">Creează postare</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-md text-muted-foreground hover:bg-muted transition-colors duration-150"
              aria-label="Închide fereastra"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="p-4 space-y-4">
            <div className="flex items-center gap-3">
              {userAvatar ? (
                <img
                  src={userAvatar}
                  alt={`Poză de profil: ${userName}`}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground text-lg font-semibold">
                  {userName.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <p className="text-sm font-semibold text-foreground">{userName}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Select
                    options={effectiveVisibilityOptions}
                    value={visibility}
                    onChange={(value) => setVisibility(value as Post['visibility'])}
                    className="text-xs"
                  />
                </div>
              </div>
            </div>

            {visibility === 'class' && classOptions.length > 0 && (
              <Select
                label="Alege clasa"
                options={classOptions}
                value={targetClassroomId}
                onChange={(value) => setTargetClassroomId(value as number)}
              />
            )}

            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="La ce te gândești?"
              className="w-full min-h-[120px] p-3 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              maxLength={5000}
            />

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {content.length}/5000 caractere
              </span>
            </div>

            {imagePreviews.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview}
                      alt={`Previzualizare ${index + 1}`}
                      className="w-full h-32 object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 p-1 bg-error text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                      aria-label="Șterge imaginea"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {videoPreview ? (
              <div className="space-y-2">
                <div className="relative overflow-hidden rounded-md border border-border bg-muted/20">
                  <video src={videoPreview} controls className="w-full max-h-[320px] bg-black" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{selectedVideos[0]?.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {selectedVideos[0] ? `${(selectedVideos[0].size / 1024 / 1024).toFixed(2)} MB` : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(0)}
                    className="p-2 text-error hover:bg-error/10 rounded-md transition-colors duration-150"
                    aria-label="Șterge video"
                  >
                    <Icon name="Trash2" size={18} />
                  </button>
                </div>
              </div>
            ) : null}

            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Adaugă etichete (max 5)"
                  className="flex-1 px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={tags.length >= 5}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddTag}
                  disabled={!tagInput.trim() || tags.length >= 5}
                  iconName="Plus"
                >
                  Adaugă
                </Button>
              </div>

              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-md"
                    >
                      #{tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:text-error transition-colors duration-150"
                        aria-label="Elimină eticheta"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <Select
              label="Categoria postării"
              options={categoryOptions}
              value={category}
              onChange={(value) => setCategory(value as Post['category'])}
            />

            {category === 'event' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <input
                    value={eventTitle}
                    onChange={(e) => setEventTitle(e.target.value)}
                    placeholder="Titlu eveniment *"
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Începe</label>
                  <input
                    type="datetime-local"
                    value={eventStartAt}
                    onChange={(e) => setEventStartAt(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Se termină</label>
                  <input
                    type="datetime-local"
                    value={eventEndAt}
                    onChange={(e) => setEventEndAt(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <input
                    value={eventLocation}
                    onChange={(e) => setEventLocation(e.target.value)}
                    placeholder="Locație (opțional)"
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    min={1}
                    value={eventCapacity}
                    onChange={(e) => setEventCapacity(e.target.value)}
                    placeholder="Capacitate (opțional)"
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div className="md:col-span-2 text-xs text-muted-foreground">
                  Înscrierile se fac din postare (buton Înscrie-te).
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted rounded-md transition-colors duration-150"
              >
                <Icon name="Image" size={20} />
                <span>Poze</span>
              </button>

              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted rounded-md transition-colors duration-150"
              >
                <Icon name="Video" size={20} />
                <span>Video</span>
              </button>

              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="hidden"
              />

              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={handleAttachmentSelect}
                className="hidden"
              />
              <div className="text-xs text-muted-foreground ml-auto">Max 5 poze, max 1 video.</div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-card border-t border-border p-4 flex items-center justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Anulează
            </Button>
            <Button variant="default" onClick={handleSubmit} disabled={!content.trim()} iconName="Send">
              Publică
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreatePostModal;
